import React, {useEffect, useState} from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import {useDispatch, useSelector} from "react-redux";
import { BsExclamationTriangleFill } from "react-icons/bs";
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {useNavigate} from "react-router-dom";
import {Alert} from "react-bootstrap";
import {Place} from "@mui/icons-material";
import {BrowserView, isMobileOnly} from "react-device-detect";
import Container from "react-bootstrap/Container";
import logging from "app/shared/utils/logging";
import {
    addItemsToCart,
    changeOrderNumber,
    changeOrderStatus,
    removeGroupItemsFromCart,
    setItemSelectedForEdit,
    removeItemsFromCart,
    submitFinalOrderPreValidate,
} from "app/store/slices/orderSlice";
import {useSubmitFinalOrderMutation, useUpdateFinalOrderMutation} from "app/services/api/mutateGateAPI";
import {showMobileFooter, toggleAuthModal, toggleCartDrawer} from "app/store/slices/authSlice";
import {RewardsDetails} from "app/features/Rewards/types/RewardsDetails";
import {FilterRewardForCart} from "app/shared/utils/helperFunctions";
import ModalLoader from "app/shared/components/ModalLoader";
import {decrypt} from "app/shared/utils/EncryptionUtility";
import getBrandName from "app/shared/utils/BrandHelper";
import {updateShowChangePickUpDetailsModal} from "app/store/slices/storeSlice";
import Constants, {
    BALANCE_MESSAGE,
    HELLO_MESSAGE,
    POINTS_MESSAGE,
} from "app/shared/constants/constants";
import RewardsBanner from 'app/features/Rewards/RewardsBanner';
import {updateCouponErrorMsg} from "app/store/slices/userSlice";
import PickupScheduleButton from 'app/shared/components/PickupScheduleButton';
import { isRewardExpired } from 'app/features/RewardsHome/RewardsUtility';
import { setHasRewaredSelected, updateSelectedReward } from 'app/store/slices/rewardsSlice';
import LABELS from 'app/shared/constants/Literals';
import ARIA_LABELS from 'constants/ariaLabels';
import CartItem from '../CartItem/CartItem';
import {ProductType} from '../types/OrderTypes';
import {createOrderRequest} from "../types/OrderRequestType";
import {RootState} from "../../../store";
import {OrderSubmitType} from "../types/OrderSubmitType";
import {FinalOrderResponse, FinalOrderResponseType} from "../types/FinalOrderResponseType";
import BrandCategoryURL from "./CateringLink";

type Props = {
    cartItems: ProductType[];
    onClose?: () => void;
};

const Cart: React.FC<Props> = ({cartItems, onClose}) => {
    const dispatch = useDispatch();
    const appInsights = useAppInsightsContext();
    const {
        pickUpDate,
        pickUpType,
        storeDescription,
        storeNumber,
        isStoreClosed,
        isStoreEligible,
    } = useSelector((state: RootState) => state.stores);
    const {
        totalCartPrice,
        orderNumber,
        enterpriseUnitId,
    } = useSelector((state: RootState) => state.orders);
    const {user, isGuest} = useSelector((state: RootState) => state.auth);
    const {
        balanceAmount, balancePoints, firstName, appliedCoupon,
    } = useSelector((state: RootState) => state.userInfo);
    const [showErrorBanner, setShowErrorBanner] = useState(false);
    const [isStoreOpen, setIsStoreOpen] = useState(true);
    const {brandTitle} = getBrandName();
    const [submitFinalOrder] = useSubmitFinalOrderMutation();
    const [updateFinalOrder] = useUpdateFinalOrderMutation();
    const navigate = useNavigate();

    const {selectedReward} = useSelector((state: RootState) => state.rewards);
    const {itemsInCart} = useSelector((state: RootState) => state.orders);
    const [applicableReward, setApplicableReward] = useState<RewardsDetails>();
    const [isOrderCreationInProgress, setIsOrderCreationInProgress] = useState(false);

    useEffect(() => {
        // set error message when user applies coupon and adds more item and proceeds to checkout
        if (appliedCoupon !== "") {
            dispatch(updateCouponErrorMsg(Constants.COUPON_ERROR_MSG));
        } else {
            dispatch(updateCouponErrorMsg(""));
        }
    }, [dispatch, appliedCoupon]);

    useEffect(() => {
        if (selectedReward) {
            setApplicableReward(FilterRewardForCart([selectedReward], itemsInCart, pickUpDate));
        }
    }, [selectedReward, itemsInCart, pickUpDate]);

    useEffect(() => {
        if (isStoreClosed || !isStoreEligible) {
            setIsStoreOpen(false);
        }
    }, [isStoreClosed, isStoreEligible]);

    const handleOrderPlacement = () => {
        let orderRewardId = applicableReward?.rewardId || null;

        // Remove reward if it has been expired
        if (isRewardExpired(selectedReward, pickUpDate)) {
            dispatch(updateSelectedReward({} as RewardsDetails));
            dispatch(setHasRewaredSelected(false));
            orderRewardId = null;
        }

        const orderBody = createOrderRequest(
            cartItems,
            totalCartPrice,
            storeDescription,
            storeNumber,
            orderRewardId,
        );
        logging.info(appInsights, "_PRE_VALIDATE_ORDER_REQ", "Final order body", orderBody);
        dispatch(submitFinalOrderPreValidate(orderBody));
        if (user) {
            setIsOrderCreationInProgress(true);
            if (cartItems?.length === 0) return;
            if (!storeNumber) {
                dispatch(toggleCartDrawer(false));
                navigate("/");
                return;
            }
            let modifiedOrderBody = {...orderBody};
            if (isGuest) {
                modifiedOrderBody = {...orderBody, reward_ids: []};
            }
            // eslint-disable-next-line
            populateOrderPreValidate(modifiedOrderBody).then(() => {
                logging.debug("PreOrderCompleted", " Order was validated");
            });
        } else {
            dispatch(toggleCartDrawer(false));
            dispatch(toggleAuthModal(true));
        }
    };
    const populateOrderPreValidate = async (orderBody: OrderSubmitType) => {
        try {
            let response: FinalOrderResponse = {} as FinalOrderResponse;
            if (user) {
                if (orderNumber && enterpriseUnitId) {
                    response = await updateFinalOrder({
                        orderBody,
                        orderNumber: orderNumber || '',
                    }).unwrap();
                } else {
                    response = await submitFinalOrder(orderBody).unwrap();
                }
                const tempValue: string = decrypt(response?.value?.toString());
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const preValidate: FinalOrderResponseType = JSON.parse(tempValue);
                logging.info(appInsights, "_PRE_VALIDATE_ORDER_RESP", "Pre Validation Response", preValidate);
                dispatch(changeOrderStatus(preValidate));
                dispatch(changeOrderNumber(preValidate?.check_number));
                setIsOrderCreationInProgress(false);
                navigate(`/confirmation/${preValidate?.check_number}`);
                dispatch(showMobileFooter(false));
                dispatch(toggleCartDrawer(false));
            } else {
                dispatch(toggleAuthModal(true));
                setIsOrderCreationInProgress(false);
            }
        } catch (e) {
            setShowErrorBanner(true);
            setIsOrderCreationInProgress(false);
            logging.error(appInsights, "_PRE_VALIDATE_ORDER_ERR", "Error placing order", e);
        }
    };

    const handleAddToCart = (clickedItem: ProductType) => {
        dispatch(addItemsToCart(clickedItem));
    };

    const handleRemoveFromCart = (item: ProductType) => {
        dispatch(removeItemsFromCart(item));
    };

    const handleRemoveGroupFromCart = (item: ProductType) => {
        dispatch(removeGroupItemsFromCart(item));
    };

    const handleItemSelectedForEdit = (item: ProductType) => {
        navigate("/menu");
        dispatch(toggleCartDrawer(false));
        dispatch(setItemSelectedForEdit(item));
    };

    const handlePickupInfoChange = () => {
        dispatch(toggleCartDrawer(false));
        dispatch(updateShowChangePickUpDetailsModal(true));
    };

    const cartAddItemsHandler = () => {
        dispatch(toggleCartDrawer(false));
        navigate("/category");
    };

    const closeCartHandler = () => {
        if (onClose) {
            onClose();
            return;
        }
        dispatch(toggleCartDrawer(false));
    };

    if (showErrorBanner) {
        return (
            <Alert variant="danger" onClose={() => setShowErrorBanner(false)} dismissible>
                <div className="d-inline-flex">
                    <BsExclamationTriangleFill size={25} className="errorImage" />
                    <Alert.Heading className="font-main errorText">{LABELS.CART.bannerError}</Alert.Heading>
                </div>
                <p className="font-sub errorText">
                    {LABELS.CART.bannerErrorMessage}
                </p>
            </Alert>
        );
    }

    return (
        <Card className="border-0 h-100">
            <Card.Header
                className="p-2 horizontalAlignCenter justify-content-between rounded-0 header-bg"
            >
                <div className="horizontalAlignCenter pt-2 pb-2">
                    <Place fontSize="large" className="map-location-icon"/>
                    <div className="moreHeaderTextContent">
                        <span className="brandName">{brandTitle}</span>
                        <span>
                            <Button
                                disabled={storeDescription === "" || storeDescription === Constants.NO_LOCATION_SELECTED}
                                variant="link"
                                className="storeLink"
                                onClick={() => {
                                    closeCartHandler();
                                    navigate("/");
                                }}
                            >
                                <strong className="user-info">{storeDescription}</strong>
                            </Button>
                        </span>
                        <span>
                            <PickupScheduleButton
                                className="button-hover ml-0 m-0 border-0 p-0 pl-0 font-sub bg-transparent"
                                onClick={handlePickupInfoChange}
                            />
                        </span>
                    </div>
                </div>
                {user && !isGuest && (
                    <BrowserView>
                        <div>
                            <div className="ms-0 p-0">
                                <p className="ms-1 user-greeting">
                                    <b>
                                        {HELLO_MESSAGE(firstName)}
                                    </b>
                                </p>
                                <p
                                    color="primary-light"
                                    className="m-0 ms-1 font-sub"
                                >
                                    {POINTS_MESSAGE(balancePoints.toString()) }
                                </p>
                                <p className="m-0 ms-1 font-sub">
                                    {BALANCE_MESSAGE(balanceAmount?.toFixed(2))}
                                </p>
                            </div>
                        </div>
                    </BrowserView>
                )}
            </Card.Header>
            <Card.Body className="overflow-auto">
                <ModalLoader showModal={isOrderCreationInProgress} />
                <BrandCategoryURL />
                {(selectedReward
                    && Object.entries(selectedReward)?.length > 0)
                    && <RewardsBanner isCartPage />}
                {cartItems?.length === 0 ? <p className="font-sub">{LABELS.CART.noItems}</p> : null}
                {cartItems.map((item, index) => (
                    <CartItem
                        key={`${item.omsData.salesItems[0].productId}${item.productSizeId}${index.toString()}`}
                        item={item}
                        addToCart={handleAddToCart}
                        removeFromCart={handleRemoveFromCart}
                        removeGroupFromCart={handleRemoveGroupFromCart}
                        editItemFromCart={handleItemSelectedForEdit}
                    />
                ))}
            </Card.Body>
            <Card.Footer
                className="d-flex verticalAlignCenter justify-content-center border-0 p-0 m-0"
            >
                <Container
                    className="d-flex flex-row justify-content-between cartTotalBgColor subtotal-font"
                >
                    <h4 className="p-3 m-0">{LABELS.CART.subtotal}</h4>
                    <h4 className="p-3 m-0">
                        <span aria-label={ARIA_LABELS.CART.subtotal}>
                            $
                            {(totalCartPrice === 0) ? '0.00' : totalCartPrice?.toFixed(2)}
                        </span>
                    </h4>
                </Container>
                <Button
                    className={`secondary-button ${isMobileOnly ? 'w-75' : 'btn-width'}`}
                    variant="secondary"
                    onClick={cartAddItemsHandler}
                    aria-label={ARIA_LABELS.CART.addItems}
                >
                {LABELS.CART.addItems}
                </Button>

            <Button
                    disabled={!isStoreOpen
                        || !(totalCartPrice > 0)
                        || pickUpType === ""
                        || pickUpType === Constants.NO_PICKUP_TYPE}
                    className={`order-now-card-button mb-3 ${
                        (!isStoreOpen || !(totalCartPrice > 0)) ? 'disable-button' : ''
                    } ${isMobileOnly ? 'w-75' : 'btn-width'}`}
                    variant="primary"
                    onClick={handleOrderPlacement}
                    aria-label={ARIA_LABELS.CART.proceedToCheckout}
            >
                    {LABELS.CART.proceedToCheckout}
            </Button>
                {!isStoreOpen && itemsInCart?.length > 0
                    && (
                        <Alert variant="danger" className="mt-2 p-0 m-0 border-0 mt-2 font-sub error-main">
                            {LABELS.CART.storeIsNotAcceptingOrders}
                        </Alert>
                    )}
            </Card.Footer>
        </Card>
    );
};
export default Cart;
